/**
 * Helper de fetch centralizado.
 * Toda comunicação com a API passa por aqui.
 *
 * Dev deploy: VITE_API_BASE=https://dev-painel.radiohabblet.com.br
 * Prod deploy: VITE_API_BASE=https://painel.radiohabblet.com.br
 * Local (vite dev): proxy /api → painel (ver vite.config.js)
 */

import { BR_OFFSET, BR_PARTS_LOCALE, BR_TIMEZONE } from '@/constants/locale.js'
import { httpFetch } from '@/services/http.js'

function normalizeRootUrl(value) {
  if (value == null || value === '') return ''
  return String(value).replace(/\/+$/, '')
}

export const BASE = (normalizeRootUrl(import.meta.env.VITE_API_BASE) || 'https://painel.radiohabblet.com.br') + '/api'
export const HABBLET_API = normalizeRootUrl(import.meta.env.VITE_HABBLET_API_BASE) || 'https://api.radiohabblet.com.br'
export const IMAGER = HABBLET_API + '/imager'

function habbletUrl(path = '', params = null) {
  const base = `${HABBLET_API}/${String(path).replace(/^\/+/, '')}`
  if (!params) return base
  const q = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== null && v !== undefined && v !== '') q.set(k, String(v))
  }
  const s = q.toString()
  return s ? `${base}?${s}` : base
}

/** Monta URL do imager com query string ou objeto de parâmetros. */
export function imagerUrl(query = '') {
  if (query && typeof query === 'object') {
    return habbletUrl('imager', query)
  }
  if (!query) return IMAGER
  return IMAGER + (query.startsWith('?') ? query : '?' + query)
}

/** Avatar padrão via API de imager. */
export function avatarUrl(user, opts = {}) {
  if (!user) return ''
  return imagerUrl({
    user,
    action: 'std',
    gesture: 'std',
    direction: 2,
    head_direction: 2,
    headonly: false,
    size: 'b',
    img_format: 'png',
    dance: 0,
    frame_num: 0,
    effect: 0,
    ...opts,
  })
}

export function badgeImageUrl(item) {
  if (!item) return ''
  if (item.image_url) return item.image_url
  if (item.code) return `https://images.habblet.city/c_images/album1584/${item.code}.gif`
  return ''
}

export function mobIconUrl(item) {
  if (!item) return ''
  if (item.image_url) return item.image_url
  const classname = item.classname ?? item.code
  if (classname) return `https://images.habblet.city/library/hof_furni/icons/${classname}_icon.png`
  return ''
}

export async function fetchJson(url, options = {}) {
  const res = await httpFetch(url, {
    credentials: options.credentials ?? 'include',
    headers: options.headers,
  })
  if (!res.ok) throw new Error(res.statusText)
  return res.json()
}

async function habbletFetchJson(path, params = {}, options = {}) {
  try {
    return await fetchJson(habbletUrl(path, params), {
      credentials: 'omit',
      ...options,
    })
  } catch (_) {
    return {}
  }
}

export async function getHabbletBadges({ page = 1, limit = 20, search = '' } = {}) {
  const params = { page, limit }
  if (search) params.search = search
  return habbletFetchJson('badges', params)
}

export async function getHabbletMobs({ page = 1, limit = 20, search = '' } = {}) {
  const params = { page, limit }
  if (search) params.search = search
  return habbletFetchJson('mobs', params)
}

export function mapHabbletBadge(item) {
  return {
    code: item.code,
    image_url: badgeImageUrl(item),
    name: item.name ?? '',
    description: item.description ?? '',
    new: !!item.new,
    discovered_at: item.discovered_at_utc ? formatBrazilDateTime(item.discovered_at_utc) : '',
  }
}

export function mapHabbletMob(item) {
  const classname = item.classname ?? item.code
  return {
    classname,
    id_mob: classname,
    code: item.code,
    image_url: mobIconUrl(item),
    name: item.name ?? '',
    description: item.description ?? '',
    new: !!item.new,
    discovered_at: item.discovered_at_utc ? formatBrazilDateTime(item.discovered_at_utc) : '',
  }
}


async function request(method, path, body = null) {
  const url = BASE + path

  const opts = {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  }
  if (body !== null) opts.body = JSON.stringify(body)
  const res = await httpFetch(url, opts)
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw Object.assign(new Error(err.message ?? res.statusText), { status: res.status, data: err })
  }

  return res.json()
}

const get  = (path) => request('GET', path)
const post = (path, body) => request('POST', path, body)
const put  = (path, body)  => request('PUT',    path, body)
const del  = (path)        => request('DELETE', path)

function brDateParts(date = new Date()) {
  const parts = new Intl.DateTimeFormat(BR_PARTS_LOCALE, {
    timeZone: BR_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(date)

  const out = {}
  for (const p of parts) {
    if (p.type !== 'literal') out[p.type] = p.value
  }
  return out
}

function parseApiDate(value) {
  if (!value) return null
  if (value instanceof Date) return value
  const raw = String(value).trim()
  if (!raw) return null
  let normalized = raw.includes('T') ? raw : raw.replace(' ', 'T')
  if (/[+-]\d{2}$/.test(normalized)) {
    normalized += ':00'
  }
  const hasTimezone = /(?:Z|[+-]\d{2}(?::?\d{2})?)$/.test(normalized)
  const date = new Date(hasTimezone ? normalized : `${normalized}${BR_OFFSET}`)
  return Number.isNaN(date.getTime()) ? null : date
}

export function formatBrazilDateTime(value) {
  const date = parseApiDate(value)
  if (!date) return ''
  const p = brDateParts(date)
  return `${p.day}/${p.month}/${p.year} ${p.hour}:${p.minute}`
}

function timeAgoBrazil(value) {
  const date = parseApiDate(value)
  if (!date) return ''
  const diff = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000))
  if (diff < 60) return 'agora'
  if (diff < 3600) {
    const minutes = Math.floor(diff / 60)
    return `${minutes} minuto${minutes === 1 ? '' : 's'}`
  }
  if (diff < 86400) {
    const hours = Math.floor(diff / 3600)
    return `${hours} hora${hours === 1 ? '' : 's'}`
  }
  const days = Math.floor(diff / 86400)
  return `${days} dia${days === 1 ? '' : 's'}`
}

function brDateString(date = new Date()) {
  const p = brDateParts(date)
  return `${p.year}-${p.month}-${p.day}`
}

export function brHour(date = new Date()) {
  return Number(brDateParts(date).hour)
}

function addDays(date, days) {
  return new Date(date.getTime() + (days * 86400000))
}

// Monta query string a partir de um objeto, filtrando nulls/undefined
function qs(params) {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== null && v !== undefined && v !== '') p.append(k, v)
  }
  const s = p.toString()
  return s ? '?' + s : ''
}

// URL base de uploads do painel
// Definido via .env / .env.production — nunca hardcoded.
export const UPLOAD_BASE = normalizeRootUrl(import.meta.env.VITE_UPLOAD_BASE ?? '/upload') || '/upload'

// Resolve path de upload retornado pela API
export function uploadUrl(path) {
  if (!path) return ''
  if (path.startsWith('http')) return path
  const segment = String(path).replace(/^\/+/, '')
  return UPLOAD_BASE + '/' + segment
}

// Calcula prev/next respeitando total. next = 0 quando não há mais página.
function paginate(pag, perPage, total) {
  const totalPages = Math.max(1, Math.ceil((total || 0) / perPage))
  const prev = pag > 1 ? pag - 1 : 0
  const next = pag < totalPages ? pag + 1 : 0
  return { prev, next, total: total || 0, totalPages }
}

// Monta query string de paginação no formato esperado pelo php-crud-api.
// Combina sempre 'page=N,size' (separar size em parâmetro distinto causa offset errado).
function pageQuery(pag, perPage) {
  return `page=${pag},${perPage}`
}

// Retorna data/hora atual no formato MySQL (YYYY-MM-DD HH:mm:ss)
export function nowMysql() {
  const p = brDateParts()
  return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}:${p.second}`
}

// ─── AUTH ────────────────────────────────────────────────────────────────────

export const auth = {
  login: (login, password) => post('/login', { login, password }),
  register: (login, password) => post('/register', { login, password }),
  logout: () => post('/logout'),
}

// ─── SESSION / USUÁRIO LOGADO ─────────────────────────────────────────────────
// php-crud-api expõe /api/me via dbAuth quando logado
export async function getLogged() {
  try {
    const data = await get('/me')
    // data = { id, username, email, ... } quando logado; erro 401 quando não
    return { logged: true, nick: data.username, user: data.username, id: data.id, score: data.score ?? 0, data }
  } catch (e) {
    if (e.status === 401 || e.status === 403) return { logged: false }
    return { logged: false }
  }
}

// ─── INIT DO SITE ─────────────────────────────────────────────────────────────
// Lê o tema ativo da tabela site_theme. Garante a invariante: 1 ativo por vez
// (via SecureAction ActivateSiteTheme + Workflow EnsureSingleActive).
export async function getInit() {
  const themeRes = await get('/records/site_theme?filter=is_active,eq,1&size=1').catch(() => ({ records: [] }))
  const theme = themeRes.records?.[0] ?? {}
  return {
    color1:      theme.color1      || '#21415C',
    color2:      theme.color2      || '#36628A',
    logo:        theme.logo        ? uploadUrl(theme.logo)        : '',
    header:      theme.header      ? uploadUrl(theme.header)      : '',
    logo_footer: theme.logo_footer ? uploadUrl(theme.logo_footer) : '',
    favicon:     theme.favicon     ? uploadUrl(theme.favicon)     : '/favicon.ico',
    body:        theme.body        ? uploadUrl(theme.body)        : '',
    clouds:      !!theme.show_clouds,
    maintenance: 0,
    code:        '',
  }
}

// ─── SECURE ACTIONS ───────────────────────────────────────────────────────────
// Helper para chamar /api/actions/{key}. Centraliza tratamento de erro/mensagem.
export async function callAction(key, input = {}) {
  try {
    const data = await post(`/actions/${key}`, input)
    return { ...data, text: data.message }
  } catch (e) {
    return { error: true, text: e.data?.message ?? 'Erro ao executar ação' }
  }
}

// Curtir locutor atual da rádio (sem expor dados internos)
export const likeAnnouncer = () => callAction('like_announcer')

// Enviar recado para usuário
export const sendUserMessage = (to, message) => callAction('send_user_message', { to, message })

// Denunciar locutor atual
export const reportAnnouncer = (motive) => callAction('report_announcer', { motive })

// Votar em enquete
export const voteInPoll = (cat_vote_id, name) => callAction('vote', { cat_vote_id, name })

// Desmarcar slot de escala (admin pode tudo, locutor só próprio)
export const unmarkScheduleSlot = (table, id) => callAction('delete_schedule_slot', { table, id })

export const checkPanelAccess = () => callAction('check_panel_access')

// ─── RÁDIO STATUS ─────────────────────────────────────────────────────────────
export async function getRadioStatus() {
  return habbletFetchJson('radio/status')
}

// ─── HELPERS DE RESOLUÇÃO (reutilizáveis) ─────────────────────────────────────

// Resolve { id: username } para uma lista de ids — 1 query, só id+username.
async function resolveUsernames(ids) {
  const unique = [...new Set((ids ?? []).filter(Boolean))]
  if (!unique.length) return {}
  const ud = await get(`/records/user?filter=id,in,${unique.join(',')}&include=id,username`)
  const map = {}
  for (const u of ud.records ?? []) map[u.id] = u.username
  return map
}

// Conta registros visíveis por chave estrangeira, em UMA query agregada.
// Traz só id+fk dos visíveis (payload mínimo) e agrupa em JS.
// Retorna { [fkValue]: count }.
async function countCommentsBy(table, fk, parentIds) {
  const ids = [...new Set((parentIds ?? []).filter(Boolean))]
  if (!ids.length) return {}
  const url = `/records/${table}?filter=${fk},in,${ids.join(',')}&filter=is_visible,eq,1&include=id,${fk}&size=1000`
  const data = await get(url).catch(() => ({ records: [] }))
  const map = {}
  for (const c of data.records ?? []) {
    map[c[fk]] = (map[c[fk]] ?? 0) + 1
  }
  return map
}

// ─── NOTÍCIAS / EVENTOS ───────────────────────────────────────────────────────
const PAGE_SIZE = 8

export async function getNews({ pag = 1, s = '', limit = PAGE_SIZE, eventosOnly = false } = {}) {
  s = (s ?? '').trim()
  const perPage = limit
  const offset  = (pag - 1) * perPage

  const filters = ['filter=is_published,eq,1']
  if (eventosOnly) filters.push('filter=is_event,eq,1')
  else filters.push('filter=is_event,eq,0')
  if (s) filters.push(`filter=title,cs,${encodeURIComponent(s)}`)

  // is_pinned primeiro (fixados no topo), depois mais recentes
  // include: só colunas usadas na listagem — NÃO traz 'text' (longtext pesado,
  // só é usado na página individual via getNewsById).
  const cols = 'id,title,url,image,description,created_by,published_at,created_at,event_at,is_pinned'
  const url = `/records/news?${filters.join('&')}&order=is_pinned,desc&order=id,desc&include=${cols}&${pageQuery(pag, perPage)}`
  const data = await get(url)
  const records = data.records ?? []
  const total   = data.results ?? records.length

  // Resolve usernames + conta comentários em paralelo (queries independentes).
  // Comentários: 1 única query (IN com os ids da página) trazendo só id,news_id
  // dos visíveis — agregação feita em JS, mesmo padrão do getTopics.
  const userIds  = [...new Set(records.map(r => r.created_by).filter(Boolean))]
  const newsIds  = records.map(r => r.id)
  const [userMap, commentsMap] = await Promise.all([
    resolveUsernames(userIds),
    countCommentsBy('comments_news', 'news_id', newsIds),
  ])

  const now = Math.floor(Date.now() / 1000)
  const news = records.map(r => {
    const eventDate = parseApiDate(r.event_at)
    const eventTs = eventDate ? Math.floor(eventDate.getTime() / 1000) : null
    return {
      id:      r.id,
      titulo:  r.title,
      url:     r.url,
      imagem:  uploadUrl(r.image),
      descricao: r.description,
      autor:   userMap[r.created_by] ?? '',
      time:    timeAgoBrazil(r.published_at ?? r.created_at),
      comments: commentsMap[r.id] ?? 0,
      fixada:  !!r.is_pinned,
      rawDate: eventTs ? eventTs - now : null,
    }
  })

  return { news, ...paginate(pag, perPage, total) }
}

export async function getNewsById(id) {
  const [data, logged_] = await Promise.all([
    get(`/records/news/${id}`),
    getLogged(),
  ])
  if (!data?.id) return [{ error: true, text: 'Notícia não encontrada' }]
  let autor = ''
  if (data.created_by) {
    const ud = await get(`/records/user/${data.created_by}?include=id,username`).catch(() => ({}))
    autor = ud.username ?? ''
  }
  return [{
    titulo: data.title,
    texto:  data.text,
    img:    uploadUrl(data.image),
    autor,
    time:   formatBrazilDateTime(data.published_at ?? data.created_at),
  }]
}

// ─── CATEGORIAS DE PÁGINAS (nav) ──────────────────────────────────────────────
export async function getPagCat() {
  const [cats, pages] = await Promise.all([
    get('/records/cat_pages?filter=is_active,eq,1&order=id,asc'),
    get('/records/pages?filter=is_published,eq,1&order=id,asc&include=id,title,url,category_id'),
  ])
  const pagesByCat = {}
  for (const p of pages.records ?? []) {
    if (!pagesByCat[p.category_id]) pagesByCat[p.category_id] = []
    // url pode estar vazio para páginas antigas — usa id como fallback de navegação
    pagesByCat[p.category_id].push({ id: p.id, title: p.title, url: p.url || String(p.id) })
  }
  return (cats.records ?? []).map(c => ({
    cat:    c.category,
    paginas: pagesByCat[c.id] ?? [],
  })).filter(c => c.paginas.length > 0)
}

// ─── PÁGINA ESTÁTICA ──────────────────────────────────────────────────────────
export async function getPagina(urlOrId) {
  const isNum = /^\d+$/.test(String(urlOrId))
  let data
  if (isNum) {
    data = await get(`/records/pages/${urlOrId}`)
  } else {
    const res = await get(`/records/pages?filter=url,eq,${encodeURIComponent(urlOrId)}&filter=is_published,eq,1&size=1`)
    data = res.records?.[0]
  }
  if (!data) return [{ error: true }]
  return [{ title: data.title, text: data.text }]
}

// ─── EVENTOS ─────────────────────────────────────────────────────────────────
// Eventos mais recentes primeiro. event_at NULL vai por último (NULLS LAST via IS NULL trick).
export async function getEvents({ pag = 1 } = {}) {
  const perPage = 3
  // include: só colunas usadas — sem 'text' nem 'description' completa desnecessária
  const cols = 'id,title,url,image,description,created_by,published_at,created_at,event_at'
  const url = `/records/news?filter=is_published,eq,1&filter=is_event,eq,1&order=event_at,desc&include=${cols}&${pageQuery(pag, perPage)}`
  const data = await get(url)
  const records = data.records ?? []
  const total   = data.results ?? records.length

  const userIds = [...new Set(records.map(r => r.created_by).filter(Boolean))]
  let userMap = {}
  if (userIds.length) {
    const ud = await get(`/records/user?filter=id,in,${userIds.join(',')}&include=id,username`)
    for (const u of ud.records ?? []) userMap[u.id] = u.username
  }

  const now = Math.floor(Date.now() / 1000)
  const news = records.map(r => {
    const eventDate = parseApiDate(r.event_at)
    const eventTs   = eventDate ? Math.floor(eventDate.getTime() / 1000) : null
    return {
      id:       r.id,
      titulo:   r.title,
      url:      r.url,
      imagem:   uploadUrl(r.image),
      descricao: r.description,
      autor:    userMap[r.created_by] ?? '',
      time:     timeAgoBrazil(r.published_at ?? r.created_at),
      rawDate:  eventTs ? eventTs - now : null,
    }
  })

  return { news, ...paginate(pag, perPage, total) }
}

// ─── ARTES / GALERIA ──────────────────────────────────────────────────────────
export async function getArts({ pag = 1, s = '', perPage: _perPage = 3 } = {}) {
  s = (s ?? '').trim()
  const perPage = _perPage
  const filters = ['filter=is_published,eq,1']
  if (s) filters.push(`filter=title,cs,${encodeURIComponent(s)}`)
  // include: só colunas usadas na grade de artes
  const url = `/records/arts?${filters.join('&')}&order=id,desc&include=id,title,url,image,created_by&${pageQuery(pag, perPage)}`
  const data = await get(url)
  const records = data.records ?? []
  const total   = data.results ?? records.length

  // arts usa created_by como autor
  const userIds = [...new Set(records.map(r => r.created_by).filter(Boolean))]
  let userMap = {}
  if (userIds.length) {
    const ud = await get(`/records/user?filter=id,in,${userIds.join(',')}&include=id,username`)
    for (const u of ud.records ?? []) userMap[u.id] = u.username
  }

  const art = records.map(r => ({
    id:     r.id,
    titulo: r.title,
    url:    r.url,
    imagem: uploadUrl(r.image),
    autor:  userMap[r.created_by] ?? '',
  }))
  return { art, ...paginate(pag, perPage, total) }
}

export async function getArtById(id) {
  const data = await get(`/records/arts/${id}`)
  if (!data?.id) return [{ error: true, text: 'Arte não encontrada' }]
  let autor = ''
  if (data.created_by) {
    const ud = await get(`/records/user/${data.created_by}?include=id,username`).catch(() => ({}))
    autor = ud.username ?? ''
  }
  return [{
    title:       data.title,
    description: data.description,
    imagem:      uploadUrl(data.image),
    autor,
    time: formatBrazilDateTime(data.published_at ?? data.created_at),
  }]
}

// ─── PODCASTS ─────────────────────────────────────────────────────────────────
export async function getPodcast({ pag = 1 } = {}) {
  const perPage = 1
  const url = `/records/podcasts?filter=is_published,eq,1&order=id,desc&include=id,title,description,audio,image&${pageQuery(pag, perPage)}`
  const data = await get(url)
  const records = data.records ?? []
  const total   = data.results ?? records.length
  const podcast = records.map(r => ({
    id:          r.id,
    title:       r.title,
    description: r.description,
    audio:       uploadUrl(r.audio),
    imagem:      uploadUrl(r.image),
  }))
  return { podcast, ...paginate(pag, perPage, total) }
}

// ─── TÓPICOS ──────────────────────────────────────────────────────────────────
export async function getTopics({ pag = 1, s = '', perPage: _perPage = 8 } = {}) {
  s = (s ?? '').trim()
  const perPage = _perPage
  const filters = ['filter=is_published,eq,1']
  if (s) filters.push(`filter=title,cs,${encodeURIComponent(s)}`)
  // include: só colunas usadas — sem 'text' (longtext, só usado em getTopicById)
  const url = `/records/topics?${filters.join('&')}&order=is_pinned,desc&order=id,desc&include=id,title,url,is_pinned,created_by&${pageQuery(pag, perPage)}`
  const data = await get(url)
  const records = data.records ?? []
  const total   = data.results ?? records.length

  // topics usa created_by como autor. Busca user (com photo) + conta comentários
  // em paralelo — queries independentes.
  const userIds  = [...new Set(records.map(r => r.created_by).filter(Boolean))]
  const topicIds = records.map(r => r.id)
  const [userMap, commentsMap] = await Promise.all([
    (async () => {
      if (!userIds.length) return {}
      const ud = await get(`/records/user?filter=id,in,${userIds.join(',')}&include=id,username,photo`)
      const map = {}
      for (const u of ud.records ?? []) map[u.id] = u
      return map
    })(),
    countCommentsBy('comments_topics', 'topic_id', topicIds),
  ])

  const topics = records.map(r => {
    const u = userMap[r.created_by] ?? {}
    return {
      id:       r.id,
      titulo:   r.title,
      url:      r.url,
      autor:    u.username ?? '',
      avatar:   uploadUrl(u.photo),
      photo:    uploadUrl(u.photo),
      comments: commentsMap[r.id] ?? 0,
      fix:      r.is_pinned ? 'true' : 'false',
      new:      false,
    }
  })
  return { topics, ...paginate(pag, perPage, total) }
}

export async function getTopicById(id) {
  const data = await get(`/records/topics/${id}`)
  if (!data?.id) return [{ error: true }]

  // topicById usa created_by como autor
  let autor = '', photo = '', badges = [], tarja_cor = '', tarja_text = ''
  if (data.created_by) {
    const ud = await get(`/records/user/${data.created_by}?include=id,username,photo`).catch(() => ({}))
    autor = ud.username ?? ''
    photo = uploadUrl(ud.photo ?? '')

    // busca badges do autor
    const userBadges = await get(`/records/user_badges?filter=user_id,eq,${data.created_by}&filter=is_active,eq,1&size=20`).catch(() => ({ records: [] }))
    const badgeIds = [...new Set((userBadges.records ?? []).map(ub => ub.badge_id).filter(Boolean))]
    if (badgeIds.length) {
      const bd = await get(`/records/badges?filter=id,in,${badgeIds.join(',')}&include=id,title,description,image`)
      badges = (bd.records ?? []).map(b => ({
        badge:       uploadUrl(b.image ?? ''),
        title:       b.title ?? '',
        description: b.description ?? '',
      }))
    }
  }

  // conta comentários
  const cd = await get(`/records/comments_topics?filter=topic_id,eq,${id}&include=id&page=1,1`)
  const comments = cd.results ?? (cd.records ?? []).length

  return [{
    id:         data.id,
    titulo:     data.title,
    texto:      data.text,
    autor,
    avatar:     photo,
    tarja_cor,
    tarja_text,
    badges,
    comments,
    modder:     1,
    modder_by:  '',
    time:       formatBrazilDateTime(data.published_at ?? data.created_at),
  }]
}

// ─── COMENTÁRIOS ─────────────────────────────────────────────────────────────
const COMMENT_TABLE = {
  news:    { table: 'comments_news',   fk: 'news_id' },
  art:     { table: 'comments_arts',   fk: 'art_id' },
  topic:   { table: 'comments_topics', fk: 'topic_id' },
  // profile usa user_messages com to_user_id como destino
  profile: { table: 'user_messages',   fk: 'to_user_id' },
}

export async function getComments(type, id) {
  const cfg = COMMENT_TABLE[type]
  if (!cfg) return { comments: [] }

  // user_messages não tem is_visible
  const visFilter = cfg.table !== 'user_messages' ? '&filter=is_visible,eq,1' : ''
  const data = await get(`/records/${cfg.table}?filter=${cfg.fk},eq,${id}${visFilter}&order=id,asc&size=200`)
  const records = data.records ?? []

  // todos usam created_by como autor (user_messages incluído, pois from_user_id foi removido)
  const authorField = 'created_by'
  const userIds = [...new Set(records.map(r => r[authorField]).filter(Boolean))]
  let userMap = {}
  if (userIds.length) {
    const ud = await get(`/records/user?filter=id,in,${userIds.join(',')}&include=id,username`)
    for (const u of ud.records ?? []) userMap[u.id] = u.username
  }

  // user_messages não tem parent_comment_id — tudo vai como root
  const hasParent = cfg.table !== 'user_messages'
  const roots = []
  const respMap = {}
  for (const c of records) {
    if (!hasParent || !c.parent_comment_id) {
      roots.push(c)
    } else {
      if (!respMap[c.parent_comment_id]) respMap[c.parent_comment_id] = []
      respMap[c.parent_comment_id].push(c)
    }
  }

  const comments = roots.map(c => ({
    id:        c.id,
    id_user:   c[authorField],  // ID do autor (para verificar permissão de apagar)
    autor:     userMap[c[authorField]] ?? '',
    text:      c.comment ?? c.message ?? '',
    time:      timeAgoBrazil(c.commented_at ?? c.sent_at ?? c.created_at),
    responses: (respMap[c.id] ?? []).map(r => ({
      id:       r.id,
      id_user:  r[authorField],
      user:     userMap[r[authorField]] ?? '',
      response: r.comment ?? r.message ?? '',
      time:     timeAgoBrazil(r.commented_at ?? r.created_at),
    })),
  }))
  return { comments }
}

export async function postComment(type, id, text) {
  const cfg = COMMENT_TABLE[type]
  if (!cfg) throw new Error('tipo inválido')
  const logged_ = await getLogged()
  if (!logged_.logged) return { error: true, text: 'Faça login primeiro' }

  let body
  if (cfg.table === 'user_messages') {
    // user_messages: created_by=quem envia, to_user_id=perfil visitado
    body = { to_user_id: Number(id), message: text }
  } else {
    body = { [cfg.fk]: Number(id), comment: text, is_visible: true }
  }
  await post(`/records/${cfg.table}`, body)
  return { text: 'Comentário enviado!', href: true }
}

export async function postCommentResponse(commentId, postId, text, type) {
  const cfg = COMMENT_TABLE[type]
  if (!cfg) throw new Error('tipo inválido')
  const logged_ = await getLogged()
  if (!logged_.logged) return { error: true, text: 'Faça login primeiro' }
  const body = { [cfg.fk]: Number(postId), comment: text, parent_comment_id: Number(commentId), is_visible: true }
  await post(`/records/${cfg.table}`, body)
  return { text: 'Resposta enviada!' }
}

// Deleta comentário próprio
export async function deleteComment(type, commentId) {
  const cfg = COMMENT_TABLE[type]
  if (!cfg) throw new Error('tipo inválido')
  const logged_ = await getLogged()
  if (!logged_.logged) return { error: true, text: 'Faça login primeiro' }
  try {
    await del(`/records/${cfg.table}/${commentId}`)
    return { text: 'Comentário removido!' }
  } catch (e) {
    return { error: true, text: 'Erro ao remover comentário' }
  }
}

// ─── COMENTÁRIOS DE TÓPICO (com perfil enriquecido) ──────────────────────────
// O commentsTopic.vue mostra dados completos do perfil de cada comentarista
export async function getTopicComments(topicId) {
  const data = await get(`/records/comments_topics?filter=topic_id,eq,${topicId}&filter=is_visible,eq,1&order=id,asc&size=200`)
  const records = data.records ?? []
  if (!records.length) return []

  // comentários de tópico usam created_by como autor
  const userIds = [...new Set(records.map(r => r.created_by).filter(Boolean))]
  let userMap = {}
  if (userIds.length) {
    const ud = await get(`/records/user?filter=id,in,${userIds.join(',')}&include=id,username,photo,background`)
    for (const u of ud.records ?? []) userMap[u.id] = u
  }

  // conta comentários por usuário (para exibir no perfil lateral)
  const commCount = {}
  for (const r of records) {
    if (r.created_by) commCount[r.created_by] = (commCount[r.created_by] ?? 0) + 1
  }

  // busca badges dos autores
  const badgeRecs = await get(`/records/user_badges?filter=user_id,in,${userIds.join(',')}&filter=is_active,eq,1&size=200`).catch(() => ({ records: [] }))
  const badgeIds = [...new Set((badgeRecs.records ?? []).map(ub => ub.badge_id).filter(Boolean))]
  let badgeMap = {}
  if (badgeIds.length) {
    const bd = await get(`/records/badges?filter=id,in,${badgeIds.join(',')}&include=id,title,description,image`)
    for (const b of bd.records ?? []) badgeMap[b.id] = b
  }
  // agrupa badges por user
  const userBadgesMap = {}
  for (const ub of badgeRecs.records ?? []) {
    if (!userBadgesMap[ub.user_id]) userBadgesMap[ub.user_id] = []
    const b = badgeMap[ub.badge_id] ?? {}
    userBadgesMap[ub.user_id].push({
      badge:       uploadUrl(b.image ?? ''),
      title:       b.title ?? '',
      description: b.description ?? '',
    })
  }

  // raiz + respostas
  const roots = []
  const respMap = {}
  for (const c of records) {
    if (!c.parent_comment_id) {
      roots.push(c)
    } else {
      if (!respMap[c.parent_comment_id]) respMap[c.parent_comment_id] = []
      respMap[c.parent_comment_id].push(c)
    }
  }

  return roots.map(c => {
    const u = userMap[c.created_by] ?? {}
    return {
      id:        c.id,
      id_user:   c.created_by,
      user:      u.username ?? '',
      avatar:    uploadUrl(u.photo ?? ''),
      tarja_cor: '',
      tarja_text: '',
      badges:    userBadgesMap[c.created_by] ?? [],
      comments:  commCount[c.created_by] ?? 0,
      texto:     c.comment ?? '',
      time:      timeAgoBrazil(c.commented_at ?? c.created_at),
      responses: (respMap[c.id] ?? []).map(r => {
        const ru = userMap[r.created_by] ?? {}
        return {
          id:       r.id,
          id_user:  r.created_by,
          user:     ru.username ?? '',
          response: r.comment ?? '',
          time:     timeAgoBrazil(r.commented_at ?? r.created_at),
        }
      }),
    }
  })
}

// ─── PERFIL ───────────────────────────────────────────────────────────────────
export async function getProfile(username) {
  const data = await get(`/records/user?filter=username,eq,${encodeURIComponent(username)}&size=1`)
  const u = data.records?.[0]
  if (!u) return { user: null }

  // Usa page=1,1 para forçar o total real (results) sem trazer 1000 registros
  // Todas as tabelas de conteúdo/comentários agora usam created_by como autor
  const [topics, arts, commNews, commTopics, commArts, msgs, userBadges] = await Promise.all([
    get(`/records/topics?filter=created_by,eq,${u.id}&include=id&page=1,1`).catch(() => ({ results: 0 })),
    get(`/records/arts?filter=created_by,eq,${u.id}&include=id&page=1,1`).catch(() => ({ results: 0 })),
    get(`/records/comments_news?filter=created_by,eq,${u.id}&include=id&page=1,1`).catch(() => ({ results: 0 })),
    get(`/records/comments_topics?filter=created_by,eq,${u.id}&include=id&page=1,1`).catch(() => ({ results: 0 })),
    get(`/records/comments_arts?filter=created_by,eq,${u.id}&include=id&page=1,1`).catch(() => ({ results: 0 })),
    // recados do perfil via ApiView segura (sem expor user_messages diretamente)
    get(`/user/views/mensagens-perfil?to_user_id=${u.id}`).catch(() => ({ records: [] })),
    get(`/records/user_badges?filter=user_id,eq,${u.id}&filter=is_active,eq,1&size=20`).catch(() => ({ records: [] })),
  ])

  const mensages = (msgs.records ?? []).map(m => ({
    id:      m.id,
    user:    m.from_username ?? '',
    mensage: m.message ?? '',
    time:    formatBrazilDateTime(m.created_at),
  }))

  // resolve badges (user_badges.badge_id é FK simples, busca badges por IDs)
  const badgeIds = [...new Set((userBadges.records ?? []).map(ub => ub.badge_id).filter(Boolean))]
  let badgeMap = {}
  if (badgeIds.length) {
    const bd = await get(`/records/badges?filter=id,in,${badgeIds.join(',')}&include=id,title,description,image`)
    for (const b of bd.records ?? []) badgeMap[b.id] = b
  }

  const badges_ = (userBadges.records ?? []).map(ub => {
    const b = badgeMap[ub.badge_id] ?? {}
    return {
      badge_imagem: uploadUrl(b.image ?? ''),
      title:        b.title ?? '',
      description:  b.description ?? '',
    }
  })

  return {
    user: {
      id:             u.id,
      nick:           u.username,
      image:          uploadUrl(u.photo),
      background:     uploadUrl(u.background),
      topics:         topics.results ?? (topics.records ?? []).length,
      artes:          arts.results ?? (arts.records ?? []).length,
      comments_news:  commNews.results ?? (commNews.records ?? []).length,
      comments_topic: commTopics.results ?? (commTopics.records ?? []).length,
      comments_art:   commArts.results ?? (commArts.records ?? []).length,
      tarja_cor:      '',
      tarja_text:     '',
      badges:         badges_,
      mensages,
    }
  }
}

// ─── HORÁRIOS ─────────────────────────────────────────────────────────────────
export async function getHours() {
  const now = new Date()
  const today = brDateString(now)
  const tomorrowDate = brDateString(addDays(now, 1))

  const safe = p => p.catch(() => ({ records: [] }))
  const [todayData, tomorrowData] = await Promise.all([
    safe(get(`/records/radio_schedule?filter=date,eq,${today}&order=hour,asc`)),
    safe(get(`/records/radio_schedule?filter=date,eq,${tomorrowDate}&order=hour,asc`)),
  ])

  const allRecs  = [...(todayData.records ?? []), ...(tomorrowData.records ?? [])]
  const userIds  = [...new Set(allRecs.map(r => r.user_id).filter(Boolean))]
  let userMap = {}
  if (userIds.length) {
    const ud = await get(`/records/user?filter=id,in,${userIds.join(',')}&include=id,username`)
    for (const u of ud.records ?? []) userMap[u.id] = u.username
  }

  const toHour = r => ({
    user:      r.user_id && userMap[r.user_id] ? userMap[r.user_id] : 'BletDJ',
    start:     r.hour,
    isAutoDJ:  !r.user_id || !userMap[r.user_id],
  })

  const currentHour = brHour(now)

  // Preenche slots com BletDJ onde não há locutor agendado
  // Para hoje: só retorna da hora atual em diante
  const fillSlots = (records, fromHour = 0) => {
    const byHour = {}
    for (const r of records) byHour[r.hour] = toHour(r)
    const slots = []
    for (let h = fromHour; h < 24; h++) {
      slots.push(byHour[h] ?? { user: 'BletDJ', start: h, isAutoDJ: true })
    }
    return slots
  }

  return {
    hours:  fillSlots(todayData.records    ?? [], currentHour),
    hours2: fillSlots(tomorrowData.records ?? [], 0),
  }
}

// ─── RANKINGS (via SecureAction get_ranking — mês atual de Brasília) ─────────

// Mapeamento de tipo público → type interno da action
const RANK_TYPE_MAP = {
  '':         'forum',
  comments:   'forum',
  locs:       'radio',
  prom:       'promocao',
  mark:       'marketing',
  prog:       'programador',
  const:      'construtor',
}

export async function getRank(limit = 3, type = '') {
  const actionType = RANK_TYPE_MAP[type] ?? 'forum'
  const data = await callAction('get_ranking', { type: actionType, limit }).catch(() => ({}))
  const records = data.ranking ?? []

  // Monta top3 (cada posição é um array com 1 item) + resto
  const top = records.slice(0, Math.min(3, limit))
  const withRank = top.map(r => [{
    rank:  r.rank,
    user:  r.username,
    count: r.count,
  }])

  const rest = records.slice(3, limit).map(r => ({
    rank:  r.rank,
    user:  r.username,
    count: r.count,
  }))

  return { rankTop: withRank, rankPlus: rest }
}

export async function getRankComments({ pag = 1 } = {}) {
  const perPage = 5
  const data = await callAction('get_ranking', { type: 'forum', limit: 100 }).catch(() => ({}))
  const all   = data.ranking ?? []
  const total = all.length
  const slice = all.slice((pag - 1) * perPage, pag * perPage)

  const comments = slice.map(r => ({
    rank:  (pag - 1) * perPage + r.rank,
    user:  r.username,
    count: r.count,
  }))

  return { comments, ...paginate(pag, perPage, total) }
}

// ─── RECORDES ─────────────────────────────────────────────────────────────────
function resolveRecordistaName(record, holderMap = {}) {
  if (record?.user_id && holderMap[record.user_id]) return holderMap[record.user_id]
  return (record?.holder_username ?? '').trim()
}

export async function getRecords({ pag = 1, s = '' } = {}) {
  s = (s ?? '').trim()
  const perPage = 15
  const filters = ['filter=is_published,eq,1']
  if (s) filters.push(`filter=mob,cs,${encodeURIComponent(s)}`)
  // include: só colunas da listagem — sem 'text' (só usado em getRecordById)
  const cols = 'id,mob,quantity,image,image_mob,code,user_id,holder_username'
  const url = `/records/records?${filters.join('&')}&order=id,desc&include=${cols}&${pageQuery(pag, perPage)}`
  const data = await get(url)
  const records_ = data.records ?? []
  const total = data.results ?? records_.length

  const holderIds = [...new Set(records_.map(r => r.user_id).filter(Boolean))]
  let holderMap = {}
  if (holderIds.length) {
    const ud = await get(`/records/user?filter=id,in,${holderIds.join(',')}&include=id,username`)
    for (const u of ud.records ?? []) holderMap[u.id] = u.username
  }

  const record = records_.map(r => ({
    id:          r.id,
    name:        r.mob,
    itens:       r.quantity,
    imagem:      uploadUrl(r.image),
    imagem_mob:  uploadUrl(r.image_mob),
    code:        r.code,
    recordista:  resolveRecordistaName(r, holderMap),
  }))
  return { record, ...paginate(pag, perPage, total) }
}

export async function getRecordById(id) {
  const data = await get(`/records/records/${id}`)
  if (!data?.id) return null

  let recordista = ''
  if (data.user_id) {
    const ud = await get(`/records/user/${data.user_id}?include=id,username`).catch(() => ({}))
    recordista = ud.username ?? ''
  }
  if (!recordista) recordista = (data.holder_username ?? '').trim()

  // registrador = quem cadastrou (created_by)
  let registrador = ''
  if (data.created_by) {
    const ud = await get(`/records/user/${data.created_by}?include=id,username`).catch(() => ({}))
    registrador = ud.username ?? ''
  }

  return {
    id:          data.id,
    mob:         data.mob,    // componente record.vue usa info.mob
    name:        data.mob,
    code:        data.code,
    itens:       data.quantity,
    text:        data.text,
    image:       uploadUrl(data.image),     // componente usa info.image
    imagem:      uploadUrl(data.image),
    image_mob:   data.image_mob ? uploadUrl(data.image_mob) : '', // componente usa info.image_mob
    img_mob:     data.image_mob ? uploadUrl(data.image_mob) : '',
    recordista,
    user:        registrador,  // componente usa info.user para quem atualizou
    registrador,
    time:        formatBrazilDateTime(data.updated_at ?? data.created_at),
  }
}

// ─── WIREDS ───────────────────────────────────────────────────────────────────
export async function getWireds({ cat = 0, search = '', pag = 1 } = {}) {
  search = (search ?? '').trim()
  const perPage = 20
  const filters = []
  if (cat && cat !== 0) filters.push(`filter=cat_id,eq,${cat}`)
  if (search) filters.push(`filter=wired,cs,${encodeURIComponent(search)}`)
  const url = `/records/wireds?${filters.join('&')}&order=id,asc&${pageQuery(pag, perPage)}`
  const data = await get(url)
  const records = data.records ?? []
  const total = data.results ?? records.length

  const wireds = records.map(r => ({
    id:          r.id,
    wired:       r.wired,
    description: r.description,
    icon:        r.icon,
    cat_id:      r.cat_id,
    text:        r.text,
  }))
  return { wireds, ...paginate(pag, perPage, total) }
}

// ─── VALORES / MOBIS ──────────────────────────────────────────────────────────
export async function getValCat() {
  const data = await get('/records/cat_values?filter=is_active,eq,1&order=id,asc')
  return (data.records ?? []).map(r => ({ id: r.id, category: r.category }))
}

export async function getMobs({ cat = 0, pag = 1, s = '' } = {}) {
  s = (s ?? '').trim()
  const perPage = 24
  const filters = ['filter=is_active,eq,1']
  if (cat && cat !== 0) filters.push(`filter=category_id,eq,${cat}`)
  if (s) filters.push(`filter=mob,cs,${encodeURIComponent(s)}`)
  // include: só colunas usadas na grade de mobis
  const cols = 'id,mob,price,icon,image,status_mob,rarity'
  const url = `/records/values?${filters.join('&')}&order=updated_at,desc&include=${cols}&${pageQuery(pag, perPage)}`
  const data = await get(url)
  const records = data.records ?? []
  const total   = data.results ?? records.length

  const valores = records.map(r => ({
    id:    r.id,
    mob:   r.mob,
    preco: r.price,
    icon:  uploadUrl(r.icon),
    image: uploadUrl(r.image),
    stats: String(r.status_mob ?? 0),
    rarity: r.rarity,
  }))
  return { valores, ...paginate(pag, perPage, total) }
}

export async function getMobById(id) {
  const data = await get(`/records/values/${id}`)
  if (!data?.id) return null

  let user = ''
  if (data.created_by) {
    const ud = await get(`/records/user/${data.created_by}?include=id,username`).catch(() => ({}))
    user = ud.username ?? ''
  }

  return {
    id:      data.id,
    name:    data.mob,   // componente usa info.name
    mob:     data.mob,
    price:   data.price, // componente usa info.price
    preco:   data.price,
    code:    data.badge_id ?? '',
    lote:    data.lote,
    ltd:     data.ltd,
    rare:    data.rarity,
    stats:   String(data.status_mob ?? 0),
    user,                // componente usa info.user
    icon:    uploadUrl(data.icon),
    image:   uploadUrl(data.image),  // componente usa info.image
    imagem:  uploadUrl(data.image),
  }
}

// ─── EMBLEMAS GRÁTIS ─────────────────────────────────────────────────────────
export async function getBadgesFree({ pag = 1 } = {}) {
  const perPage = 9
  const url = `/records/badge_free?filter=is_active,eq,1&order=id,desc&${pageQuery(pag, perPage)}`
  const data = await get(url)
  const records = data.records ?? []
  const total   = data.results ?? records.length

  // badge_free já tem campo url diretamente; usa news_id como id pra navegação
  const badges = records.map(r => ({
    id:    r.news_id ?? 0,
    url:   r.url ?? '',
    code:  r.code,
    item:  r.item,
  }))
  return { badges, ...paginate(pag, perPage, total) }
}

// ─── ENQUETES / VOTOS ─────────────────────────────────────────────────────────
export async function getCampanha() {
  const data = await get('/records/campaigns?filter=is_active,eq,1&order=id,desc&size=1')
  return data.records ?? []
}

export async function getVoteCats(campaignId) {
  const data = await get(`/records/cat_votes?filter=campaign_id,eq,${campaignId}&filter=is_active,eq,1`)
  return (data.records ?? []).map(r => ({
    id:    r.id,
    title: r.title,
    names: (r.names ?? '').split(',').map(n => n.trim()).filter(Boolean),
  }))
}

export async function getVoteNames(catId) {
  const data = await get(`/records/cat_votes/${catId}`)
  return (data.names ?? '').split(',').map(n => n.trim()).filter(Boolean)
}

// Alias retro-compatível: usa a SecureAction internamente.
export const vote = (catId, name) => voteInPoll(catId, name)

// ─── FANSITES (Hot 5) ────────────────────────────────────────────────────────
export async function getFansites() {
  const data = await get('/records/fansites?filter=is_active,eq,1&order=listeners,desc&size=5').catch(() => ({ records: [] }))
  return (data.records ?? []).map((f, i) => ({
    rank:      i + 1,
    fasite:    f.name ?? '',
    icon:      uploadUrl(f.icon ?? ''),
    ouvintes:  f.listeners ?? 0,
    has_stream: f.has_streaming ?? false,
    ip:        f.fs_ip ?? '',
    port:      f.fs_port ?? 0,
  }))
}

// ─── EQUIPE ───────────────────────────────────────────────────────────────────
// Retorna lista de cargos agrupados por categoria hierárquica.
// Estrutura: [{ category: 'Gestão', team_order: 1, cargos: [{ id: 'ceo', cargo: 'CEO' }] }, ...]
//
// Dados de categoria (nome, team_order, show_in_team) vêm da ApiView via
// panel_role_category — gerenciável pelo painel, sem hardcode aqui.
async function _loadEquipe() {
  const data = await get('/user/views/equipe').catch(() => null)
  if (Array.isArray(data)) return data
  if (data && Array.isArray(data.records)) return data.records
  return []
}

export async function getEquipeCats() {
  const data = await _loadEquipe()

  // Filtra só cargos com usuários e que devem aparecer na equipe (show_in_team=true)
  const filtered = data.filter(g => g.show_in_team && g.users?.length > 0)

  // Agrupa por categoria — nome e order vêm do banco via ApiView
  const byCat = {}
  for (const g of filtered) {
    const catName  = g.category_display_name || 'Outros'
    const catOrder = g.team_order ?? 9999
    if (!byCat[catName]) {
      byCat[catName] = { order: catOrder, cargos: [] }
    }
    byCat[catName].cargos.push({ id: g.role, cargo: g.display_name })
  }

  // Ordena categorias pelo team_order (menor = topo), retorna estrutura final
  return Object.entries(byCat)
    .sort(([, a], [, b]) => a.order - b.order)
    .map(([category, { cargos }]) => ({ category, cargos }))
}

// Retorna usuários de um cargo específico (pelo role)
export async function getEquipeUsers(role) {
  const data = await _loadEquipe()
  const grupo = data.find(g => g.role === role)
  if (!grupo) return []
  return grupo.users.map(u => ({ user: u.username, id: u.id, photo: uploadUrl(u.photo) }))
}

// ─── CONTA / CONFIGURAÇÕES ────────────────────────────────────────────────────
export async function getMyArts(userId) {
  const own = userId ? `&filter=created_by,eq,${userId}` : ''
  return get(`/records/arts?filter=is_published,eq,1${own}&order=id,desc&page=1,50`)
}

export async function deleteMyArt(id) {
  return del(`/records/arts/${id}`)
}

export async function getMyTickets() {
  return get('/records/tickets?order=id,desc&page=1,20')
}

// ─── SEARCH ───────────────────────────────────────────────────────────────────
export async function search(text) {
  const [news_, arts_] = await Promise.all([
    get(`/records/news?filter=title,cs,${encodeURIComponent(text)}&filter=is_published,eq,1&order=id,desc&size=5&include=id,title,url,image`),
    get(`/records/arts?filter=title,cs,${encodeURIComponent(text)}&filter=is_published,eq,1&order=id,desc&size=5&include=id,title,url,image`),
  ])
  const result = []
  for (const n of news_.records ?? []) {
    result.push({ id: n.id, titulo: n.title, url: n.url, imagem: uploadUrl(n.image), router: '/noticia', name: 'News' })
  }
  for (const a of arts_.records ?? []) {
    result.push({ id: a.id, titulo: a.title, url: a.url, imagem: uploadUrl(a.image), router: '/arte', name: 'Arte' })
  }
  return result
}
